import { useEffect, useMemo } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import { toast } from "sonner";

import { CCard, CCardBody } from "@coreui/react";

import { phieuChamDiemHHApi } from "src/1/apis/phieu_cham_diem_hang_hoa.api";
import { history } from "src/App";
import { useMobileDevice } from "src/utils/hooks/viewport";

import { FormTable, FormToolbar } from "../../components";
import { defaultValues, resolver } from "../../form";

const SuaPhieuChamDiemHangHoa = () => {
  //#region Data
  const params = useParams();

  const { data, isError } = useQuery({
    queryKey: ["chi-tiet-phieu-cham-diem-hang-hoa", params?.id],
    queryFn: () => phieuChamDiemHHApi.getById(params?.id),
    enabled: !!params?.id,
    select: (response) => response?.data?.data,
  });

  if (isError) {
    toast.error("Không thể lấy thông tin phiếu chấm điểm hàng hóa!");
    history.replace("/quality-of-goods/rating-forms/list");
  }

  const { control, handleSubmit, reset } = useForm({
    mode: "all",
    defaultValues,
    resolver,
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "goods",
    keyName: "__id",
  });

  const watchGoods = useWatch({ control, name: "goods" });

  const controlledGoods = fields.map((field, index) => {
    return {
      ...field,
      ...watchGoods[index],
    };
  });

  const isSelectedAll = useMemo(
    () => controlledGoods?.length && controlledGoods?.every((e) => e?.selected),
    [controlledGoods],
  );

  const selectedList = useMemo(
    () => controlledGoods.filter((e) => e?.selected),
    [controlledGoods],
  );

  const isMobile = useMobileDevice();
  //#endregion

  //#region Event
  const onAddGood = () => {
    append({
      selected: false,
      code: "",
      name: "",
      quality: 5,
      weight: 5,
      size: 5,
      color: 5,
      flavor: 5,
      customer_review: 5,
      pricing: 5,
      materials: [],
      files: [],
    });
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const payload = {
        ...values,
        goods: values?.goods?.map((e) => ({
          ...e,
          files: e?.files?.map((el) => el?.id),
        })),
      };
      await phieuChamDiemHHApi.update(params.id, payload);

      toast.success("Sửa phiếu chấm điểm hàng hóa thành công!");
      reset(defaultValues);
      history.push("/quality-of-goods/rating-forms/list");
    } catch (error) {
      toast.error(
        error?.message ?? "Sửa phiếu chấm điểm hàng hóa không thành công!",
      );
    }
  });

  const onSelectAll = (value) => {
    const newGoods = controlledGoods.map((e) => ({
      ...e,
      selected: value,
    }));
    replace(newGoods);
  };

  const onRemove = () => {
    let removeIndexs = [];
    controlledGoods.forEach((e, i) => {
      if (e.selected) {
        removeIndexs.push(i);
      }
    });
    remove(removeIndexs);
  };
  //#endregion

  useEffect(() => {
    if (data) {
      reset({ ...data });
    }
  }, [data]);

  //#region Render
  return (
    <>
      <CCard className={classNames("toolbar", !isMobile && "sticky")}>
        <CCardBody>
          <FormToolbar
            control={control}
            onSubmit={onSubmit}
            onAddGood={onAddGood}
            canRemove={selectedList?.length}
            onRemove={onRemove}
          />
        </CCardBody>
      </CCard>

      <CCard>
        <CCardBody className="px-0 pt-4">
          <FormTable
            control={control}
            dataTable={controlledGoods}
            isSelectedAll={isSelectedAll}
            onSelectAll={onSelectAll}
          />
        </CCardBody>
      </CCard>
    </>
  );
  //#endregion
};
export default SuaPhieuChamDiemHangHoa;
